import ToggleSwitch from "Common/ToggleSwitch";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Button, Modal, Table } from "react-bootstrap";
import { authServices } from "Utils/auth/services";
import snackbarUtil from "Utils/snackBarUtil";

interface RootObject {
  user_name: string;
  parent_id: null;
  data: Data;
  status: boolean;
}

interface Data {
  users: User[];
  metadata: Metadata;
}

interface Metadata {
  total: Total[];
  own: Total[];
  parent: any[];
  teams: string[];
  columns: string[];
  total_exposure: Totalexposure[];
}

interface Totalexposure {
  market_id: string;
  selection_id: number;
  selection_name: string;
  sort_priority: number;
  win_loss: number;
  win_loss_total_exposure: number;
}

interface Total {
  selection_id: number;
  selection_name: string;
  win_loss: number;
}

interface User {
  user_id: string;
  user_type_id: number;
  user_name: string;
  domain_name: string;
  england: number;
  england_total_exposure: number;
  australia: number;
  australia_total_exposure: number;
}

interface BookModalProps {
  show: any;
  clickHandler: any;
  marketIds: string;
  setFancy: Dispatch<SetStateAction<boolean>>;
  fancy: boolean;
  marketName: string;
  screenWidth: number
}


const BookModal = ({
  show,
  clickHandler,
  marketIds,
  setFancy,
  fancy,
  marketName,
  screenWidth,
}: BookModalProps) => {
  const [bookData, setBookData] = useState<RootObject>();
  const [BookFancyData, setBookFancyData] = useState<any>([]);
  const [userData, setUserData] = useState<string>("");
  const userId = localStorage.getItem("userId");

  useEffect(() => {
    if (userId) setUserData(userId);
  }, [userId, show]);

  const getMarketPosition = async (user_id: string) => {
    const { response } = await authServices.getMarketPosition({
      user_id,
      market_id: marketIds,
    });
    if (response?.data) setBookData(response);
    else if (!response?.status) snackbarUtil.error(response?.msg);
  };

  useEffect(() => {
    if (marketIds && userData) getMarketPosition(userData);
  }, [userId, marketIds]);

  const normalizeTeamName = (team: string): string => {
    return team.toLowerCase().replace(/ /g, "_");
  };

  useEffect(() => {
    if (!bookData?.data) return;

    const { users } = bookData.data;
    const exposures = bookData.data.metadata.total_exposure;

    const groupedByPriority: { [priority: number]: any } = {};

    exposures.forEach((exposureItem) => {
      const { selection_name, sort_priority } = exposureItem;
      const normalizedTeam = normalizeTeamName(selection_name);

      const teamUsers = users.map((user: any) => ({
        user_id: user.user_id,
        user_name: user.user_name,
        user_type_id: user.user_type_id,
        domain_name: user.domain_name,
        win_loss: user[normalizedTeam] || 0,
        total_exposure: user[`${normalizedTeam}_total_exposure`] || 0,
      }));

      groupedByPriority[sort_priority] = {
        sort_priority,
        selection_name,
        users: teamUsers,
      };
    });

    const sortedData = Object.values(groupedByPriority).sort(
      (a: any, b: any) => a.sort_priority - b.sort_priority
    );

    setBookFancyData(sortedData);
  }, [bookData]);

  const handleParentShere = (ids: string | null | undefined) => {
    if (ids) {
      setUserData(ids);
      getMarketPosition(ids);
    }
  };

  const getWinLossByTeam = (team: string, dataArray: Total[]): number => {
    const teamData = dataArray.find((item) => item.selection_name === team);
    return teamData ? teamData.win_loss : 0;
  };

  const handleClose = () => {
    clickHandler();
    setUserData("");
  };

  return (
    <Modal
      size="lg"
      show={show}
      onHide={handleClose}
      className="zoomIn"
      scrollable
      centered={screenWidth < 800}
    >
      <Modal.Header className="modal-title fw-bold d-flex justify-content-between pb-1">
        <div className="d-flex align-items-center flex-wrap">
          <i
            className="ri-arrow-left-line me-2 cursor-pointer"
            onClick={() => handleParentShere(bookData?.parent_id)}
          ></i>
          Market Position
          <span className="text-muted fs-xs fw-normal mx-2">
            ({marketName})
          </span>
          <ToggleSwitch
            On="S"
            Off="F"
            id="fancyId"
            onChange={() => setFancy(!fancy)}
            checked={fancy}
          />
        </div>
        <Button variant="light btn-sm" onClick={handleClose}>
          <i className="ri-close-line fs-xl align-middle"></i>
        </Button>
      </Modal.Header>

      <Modal.Body className="pt-1 fs-md">
        <div className="table-responsive">
          <Table className="align-middle table-striped table-nowrap mb-0">
            <thead>
              <tr>
                <th>Domain Name</th>
                <th>Account</th>
                {BookFancyData.map((team: any, i: number) => (
                  <th key={i}>{team.selection_name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {BookFancyData[0]?.users.map((user: any, idx: number) => (
                <tr key={idx}>
                  <td>{user.domain_name}</td>
                  <td>
                    <div
                      className="cursor-pointer"
                      onClick={() => handleParentShere(user.user_id)}
                    >
                      <Button
                        variant="subtle-primary"
                        className="rounded-pill p-1"
                        style={{ width: "30px" }}
                      >
                        {user.user_type_id === 1 ? "C" : "A"}
                      </Button>
                      <span className="text-primary ms-2">{user.user_name}</span>
                    </div>
                  </td>
                  {BookFancyData.map((team: any, index: number) => {
                    const userData = team.users.find((u: any) => u.user_id === user.user_id);
                    const value = fancy
                      ? userData?.win_loss
                      : userData?.total_exposure;
                    return (
                      <td
                        key={index}
                        className={value < 0 ? "text-danger" : "text-success"}
                      >
                        {Number(value).toFixed(2)}
                      </td>
                    );
                  })}
                </tr>
              ))}

              {/* TOTAL Row */}
              <tr>
                <td></td>
                <td className="fw-bold">Total</td>
                {BookFancyData.map((team: any, index: number) => {
                  const teamName = team.selection_name;
                  const totalWinLoss = getWinLossByTeam(
                    teamName,
                    bookData?.data?.metadata.total || []
                  );

                  const totalExposure = team.users.reduce(
                    (acc: number, u: any) =>
                      acc + (fancy ? u.win_loss : u.total_exposure),
                    0
                  );

                  const value = fancy ? totalWinLoss : totalExposure;
                  return (
                    <td
                      key={index}
                      className={value < 0 ? "text-danger" : "text-success"}
                    >
                      {Number(value).toFixed(2)}
                    </td>
                  );
                })}
              </tr>

              {/* OWN Row */}
              {fancy && (
                <tr>
                  <td></td>
                  <td className="fw-bold">Own</td>
                  {BookFancyData.map((team: any, index: number) => {
                    const ownWinLoss = getWinLossByTeam(
                      team.selection_name,
                      bookData?.data?.metadata.own || []
                    );
                    return (
                      <td
                        key={index}
                        className={
                          ownWinLoss < 0 ? "text-danger" : "text-success"
                        }
                      >
                        {Number(ownWinLoss).toFixed(2)}
                      </td>
                    );
                  })}
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default BookModal;

